// Play — the live table. Wires the WebSocket to the narration stream, party rail,
// combat tracker, composer, dice tray, compendium, character drawer, plus rest &
// level-up flows.
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import type {
  AuthUser, Combat, Combatant, FullCharacter, PartyMember, WsClientMessage,
} from "../lib/types";
import { useGameSocket, type ChatMessage } from "../hooks/useGameSocket";
import { useMeta } from "../hooks/useMeta";
import { NarrationStream } from "./NarrationStream";
import { Composer } from "./Composer";
import { PartyRail } from "./PartyRail";
import { CombatTracker } from "./CombatTracker";
import { CombatActionBar } from "./CombatActionBar";
import { DiceTray } from "./DiceTray";
import { CompendiumDrawer } from "./CompendiumDrawer";
import { CharacterSheet, type SheetActions } from "./CharacterSheet";
import { AddItemPicker } from "./AddItemPicker";
import { LevelUpModal } from "./LevelUpModal";
import { CharacterEditModal } from "./CharacterEditModal";
import { PixelArt } from "./PixelArt";
import { QuestJournal } from "./QuestJournal";
import { RollPrompt } from "./RollPrompt";
import { RestModal } from "./RestModal";
import { CenterLoad, Drawer, HpBar } from "./ui";
import { xpQualifiesForLevel } from "./srdBudgets";
import { useToast } from "../hooks/useToast";

// Matches a CSS media query in JS so the rail render paths can mirror the stylesheet's
// ≤1080px breakpoint (below which the rails collapse into drawers). Initialised from the
// current match so there's no wrong-layout flash on first paint.
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches,
  );
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}

export function Play({ gameId, onExit }: { gameId: string; onExit: () => void }) {
  const toast = useToast();
  const { meta } = useMeta();
  const { conn, game, snapshot, stream, thinking, error, clearError, send, you, present, chat, art, turnToast, chimeEnabled, setChimeEnabled } = useGameSocket(gameId);
  const [showCompendium, setShowCompendium] = useState(false);
  const [sheetChar, setSheetChar] = useState<FullCharacter | null>(null);
  const [sheetLoading, setSheetLoading] = useState(false);
  const [levelUpChar, setLevelUpChar] = useState<FullCharacter | null>(null);
  const [editChar, setEditChar] = useState<FullCharacter | null>(null);
  const [showRest, setShowRest] = useState(false);
  const [sheetBusy, setSheetBusy] = useState(false);
  const [addingItem, setAddingItem] = useState(false);
  const [showTools, setShowTools] = useState(false);
  // Narrow-width rail drawers: the left (party) and right (scene/combat) rails collapse
  // into on-demand drawers below the CSS breakpoint.
  const narrow = useMediaQuery("(max-width: 1080px)");
  const [openDrawer, setOpenDrawer] = useState<"party" | "scene" | null>(null);

  useEffect(() => {
    if (error) { toast.push(error, "error"); clearError(); }
  }, [error]); // eslint-disable-line

  // Surface the your-turn nudge the socket builds (the hook owns the tab-title flash +
  // optional chime and auto-clears itself; we just make the alert visible).
  useEffect(() => {
    if (turnToast) toast.push(turnToast.text, "info");
  }, [turnToast?.nonce]); // eslint-disable-line

  // Widening past the breakpoint restores the inline rails, so any open rail-drawer is
  // now redundant — close it.
  useEffect(() => { if (!narrow) setOpenDrawer(null); }, [narrow]);

  const busy = thinking !== null;
  const hasStarted = stream.length > 0 || (game?.log_len ?? 0) > 0;

  const aiCharacterIds = useMemo(() => {
    const set = new Set<string>();
    game?.seats.forEach((s) => { if (s.kind === "ai" && s.character_id) set.add(s.character_id); });
    return set;
  }, [game]);

  // Human seats THIS client may act as — mirrors the roll-gating rule (owner; or admin;
  // or an unclaimed/solo seat open to anyone). Drives whose conductor spotlight reads as
  // "your turn" vs another human's.
  const controllableCharIds = useMemo(() => {
    const set = new Set<string>();
    for (const s of game?.seats ?? []) {
      if (s.kind !== "human" || !s.character_id) continue;
      if (!s.user_id) { set.add(s.character_id); continue; } // unclaimed / solo → anyone
      if (you?.is_admin) { set.add(s.character_id); continue; } // host may act for anyone
      if (you && s.user_id === you.id) set.add(s.character_id);
    }
    return set;
  }, [game, you]);

  // Pending DM-requested rolls for characters THIS user controls. A roll shows its
  // dice button to: the seat's owner; OR (admins / unclaimed seats / solo play) anyone,
  // matching the same seat-ownership rule the engine uses for actions. The DC is never sent.
  const myPendingRolls = useMemo(() => {
    const rolls = snapshot?.pending_rolls ?? [];
    if (rolls.length === 0) return [];
    const seatByChar = new Map((game?.seats ?? []).map((s) => [s.character_id, s]));
    return rolls.filter((pr) => {
      const seat = seatByChar.get(pr.character_id);
      if (!seat) return true;                 // no seat record — open to anyone present
      if (!seat.user_id) return true;         // unclaimed seat — anyone may roll (back-compat/solo)
      if (you?.is_admin) return true;         // admin (host) may roll for anyone
      return !!you && seat.user_id === you.id; // else: only the owner
    });
  }, [snapshot?.pending_rolls, game, you]);

  // Conductor spotlight (K5): the DM's baton. Out of combat only (in a fight the combat
  // turn UI owns ordering). Resolves `awaiting` to one of: a character we control ("mine"),
  // another human, an AI companion, or an open floor. The banner + composer emphasis key
  // off this; AI spotlights render nothing (the thinking indicator already covers them).
  const spotlight = useMemo(() => {
    if (snapshot?.combat) return null;
    const cue = snapshot?.spotlight_cue ?? "";
    const aw = snapshot?.awaiting ?? null;
    if (!aw) return { kind: "open" as const, name: "", cue, charId: null as string | null };
    const name =
      game?.seats.find((s) => s.character_id === aw)?.character?.name
      ?? snapshot?.party.find((p) => p.id === aw)?.name
      ?? "someone";
    if (controllableCharIds.has(aw)) return { kind: "mine" as const, name, cue, charId: aw };
    if (aiCharacterIds.has(aw)) return { kind: "ai" as const, name, cue, charId: aw };
    return { kind: "other" as const, name, cue, charId: aw };
  }, [snapshot?.combat, snapshot?.awaiting, snapshot?.spotlight_cue, snapshot?.party, game, controllableCharIds, aiCharacterIds]);

  async function openSheet(characterId: string) {
    setSheetLoading(true);
    try { setSheetChar(await api.character(gameId, characterId)); }
    catch { toast.push("Couldn't load sheet", "error"); }
    finally { setSheetLoading(false); }
  }

  async function refreshSheet(characterId: string) {
    try { setSheetChar(await api.character(gameId, characterId)); } catch { /* ignore */ }
  }

  // Open the level-up flow straight from a rail row: fetch the full sheet, then hand it
  // to the existing LevelUpModal (renders once `levelUpChar && meta`).
  async function openLevelUp(characterId: string) {
    try { setLevelUpChar(await api.character(gameId, characterId)); }
    catch { toast.push("Couldn't open level-up", "error"); }
  }

  // Which seated PCs have banked enough XP to advance. Needs `xp` in the party snapshot
  // (optional field); when the backend omits it the set stays empty and no badge lights.
  const levelUpIds = useMemo(() => {
    const s = new Set<string>();
    for (const p of snapshot?.party ?? []) {
      if (p.xp != null && xpQualifiesForLevel(p.level, p.xp)) s.add(p.id);
    }
    return s;
  }, [snapshot?.party]);

  // Celebrate an actual level-up: when a PC's level rises between snapshots, toast it for
  // the whole table. Baseline captured on first sight so a fresh mount doesn't fire.
  const prevLevels = useRef<Record<string, number>>({});
  useEffect(() => {
    for (const p of snapshot?.party ?? []) {
      const prev = prevLevels.current[p.id];
      if (prev != null && p.level > prev) {
        toast.push(`⭐ ${p.name} reached level ${p.level}!`, "success");
      }
      prevLevels.current[p.id] = p.level;
    }
  }, [snapshot?.party]); // eslint-disable-line

  // In-play sheet actions. Each calls the API, updates the open sheet from the
  // returned character, and surfaces feedback. State broadcasts handle the rails.
  function makeSheetActions(ch: FullCharacter): SheetActions {
    const run = async (fn: () => Promise<{ character: FullCharacter }>, ok?: string) => {
      setSheetBusy(true);
      try {
        const res = await fn();
        setSheetChar(res.character);
        if (ok) toast.push(ok, "success");
      } catch (e) {
        toast.push(e instanceof Error ? e.message : "Action failed", "error");
      } finally {
        setSheetBusy(false);
      }
    };
    return {
      busy: sheetBusy,
      onEquip: (index, name, equipped) =>
        run(() => api.equip(gameId, ch.id, { index, name, equipped })),
      onUseItem: (index, name) =>
        run(async () => {
          const r = await api.useItem(gameId, ch.id, { index, name });
          if (r.effect?.type === "heal") toast.push(`${name}: +${r.effect.healed} HP`, "success");
          return r;
        }),
      onCast: (spellIndex, _level, name) =>
        run(async () => {
          const r = await api.cast(gameId, ch.id, { spell_index: spellIndex });
          const at = r.cast_at_level ? ` (L${r.cast_at_level})` : r.is_cantrip ? " (cantrip)" : "";
          toast.push(`Cast ${name}${at}`, "success");
          return r;
        }),
      onRestoreSlot: (level) =>
        run(() => api.restoreSlot(gameId, ch.id, level), `Restored a level ${level} slot`),
      onRemoveItem: (index, name) =>
        run(() => api.removeItem(gameId, ch.id, { index, name })),
      onAttune: (index, name, attuned) =>
        run(() => api.attune(gameId, ch.id, { index, name, attuned }),
            attuned ? `Attuned to ${name}` : `Unattuned ${name}`),
    };
  }

  async function handleAddItem(index: string, name: string, base?: string) {
    if (!sheetChar) return;
    setSheetBusy(true);
    try {
      const res = await api.addItem(gameId, sheetChar.id, { index, name, base });
      setSheetChar(res.character);
      toast.push(`Added ${name}`, "success");
    } catch (e) {
      toast.push(e instanceof Error ? e.message : "Couldn't add item", "error");
    } finally {
      setSheetBusy(false);
      setAddingItem(false);
    }
  }

  if (!game) {
    return (
      <div className="screen">
        <div className="topbar">
          <button className="btn btn--ghost btn--sm" onClick={onExit}>← Leave</button>
          <div className="topbar__spacer" />
          <ConnPill conn={conn} />
        </div>
        <CenterLoad label="Joining the table…" />
      </div>
    );
  }

  const inCombat = !!snapshot?.combat;
  const party = snapshot?.party ?? [];

  // Right-rail body — persistent scene art plus the combat tracker (in a fight) or the
  // quest journal (exploring). Extracted so the SAME markup serves the inline wide-screen
  // rail and the narrow-screen "Scene" drawer with no duplication.
  const rightRail = hasStarted ? (
    <>
      <div className="rail-section scene-rail">
        <div className="rail-head"><span>The Scene</span></div>
        <PixelArt
          key={snapshot?.scene || "scene"}
          shape="scene"
          className="scene-art scene-art--rail"
          alt="current scene"
          body={{ kind: "scene", game_id: gameId }}
          liveUrl={art["scene:scene"]}
        />
        {snapshot?.scene && <p className="scene-rail__caption">{snapshot.scene}</p>}
      </div>
      {snapshot?.combat ? (
        <CombatTracker combat={snapshot.combat} busy={busy} send={send} seats={game.seats} you={you} />
      ) : (
        <QuestJournal gameId={gameId} quests={snapshot?.quests ?? []} />
      )}
    </>
  ) : null;

  return (
    <>
      <div className="topbar">
        <button className="btn btn--ghost btn--sm" onClick={onExit}>← Leave</button>
        <div className="topbar__crumbs">
          <span className="brand"><span className="brand__flame">🔥</span></span>
          <span>{game.name}</span>
        </div>
        {/* Rail drawer toggles — CSS reveals these only below ~1080px. */}
        <button type="button" className="drawer-toggle" onClick={() => setOpenDrawer("party")}
          aria-label="Show the party">
          ☰ Party{party.length > 0 && <span className="drawer-toggle__badge">{party.length}</span>}
        </button>
        {hasStarted && (
          <button type="button" className="drawer-toggle" onClick={() => setOpenDrawer("scene")}
            aria-label={inCombat ? "Show combat" : "Show the scene"}>
            {inCombat ? "⚔ Combat" : "🗺 Scene"}
            {inCombat && snapshot?.combat && (
              <span className="drawer-toggle__badge">R{snapshot.combat.round}</span>
            )}
          </button>
        )}
        <div className="topbar__spacer" />
        <button className="btn btn--ghost btn--sm" disabled={inCombat} title={inCombat ? "Can't rest during combat" : "Rest"}
          onClick={() => setShowRest(true)}>🏕 Rest</button>
        <button className="btn btn--ghost btn--sm" onClick={() => setShowCompendium(true)}>📖 Compendium</button>
        <button className={`btn btn--ghost btn--sm${showTools ? " is-active" : ""}`}
          onClick={() => setShowTools((s) => !s)} title="Dice roller & table chat">🎲 Dice & Table</button>
        <button className="btn btn--ghost btn--sm" onClick={() => setChimeEnabled(!chimeEnabled)}
          title={chimeEnabled ? "Turn sound on new beats — click to mute" : "Turn sound muted — click to enable"}
          aria-label={chimeEnabled ? "Mute turn sound" : "Enable turn sound"}>
          {chimeEnabled ? "🔔" : "🔕"}</button>
        <PresencePill present={present} />
        <ConnPill conn={conn} />
      </div>

      <div className={`play${inCombat ? " play--combat" : hasStarted ? " play--full" : " play--calm"}`}>
        {/* Left rail (party). At narrow widths it moves into the "Party" drawer below. */}
        {!narrow && (
          <PartyRail snapshot={snapshot} aiCharacterIds={aiCharacterIds}
            onOpenSheet={openSheet} send={send} busy={busy} />
        )}

        <div className="play__center">
          <NarrationStream stream={stream} thinking={thinking} scene={snapshot?.scene} />
          {myPendingRolls.map((pr) => (
            <RollPrompt key={pr.id} roll={pr} onRoll={send} />
          ))}
          {/* Persistent one-line combat strip. CSS shows it only when the tracker rail is
              collapsed (≤1080px), so turn awareness survives with the rail hidden. */}
          {snapshot?.combat && <MiniTracker combat={snapshot.combat} />}
          {snapshot?.combat && (
            <CombatActionBar gameId={gameId} game={game} snapshot={snapshot} busy={busy} send={send} you={you} />
          )}
          {hasStarted && spotlight && <SpotlightBanner spotlight={spotlight} />}
          <Composer game={game} hasStarted={hasStarted} busy={busy} send={send} you={you} inCombat={inCombat}
            spotlightMine={spotlight?.kind === "mine" ? spotlight.charId : null}
            spotlightDim={spotlight?.kind === "other"} />
        </div>

        {/* Right rail = campaign sidebar (scene art + tracker/journal). At narrow widths it
            moves into the "Scene" drawer below. Only present once the session has opened. */}
        {!narrow && rightRail && <div className="play__right">{rightRail}</div>}
      </div>

      {/* Narrow-width rail drawers. The right rail reuses `rightRail` verbatim; the party
          list is a compact drawer variant (PartyRail hardcodes the `.play__left` class the
          media query hides, so it can't be dropped into a drawer without a change to it). */}
      {narrow && openDrawer === "party" && (
        <Drawer title="The Party" side="left" onClose={() => setOpenDrawer(null)}>
          <DrawerPartyList
            party={party}
            aiCharacterIds={aiCharacterIds}
            levelUpIds={levelUpIds}
            spotlightId={snapshot?.combat ? null : (snapshot?.awaiting ?? null)}
            busy={busy}
            send={send}
            onOpenSheet={(id) => { setOpenDrawer(null); openSheet(id); }}
            onLevelUp={(id) => { setOpenDrawer(null); openLevelUp(id); }}
          />
          {snapshot && snapshot.party_gold > 0 && (
            <div className="rail-section">
              <div className="rail-head"><span>Party Coin</span></div>
              <span className="chip chip--gold">{snapshot.party_gold} gp</span>
            </div>
          )}
        </Drawer>
      )}
      {narrow && openDrawer === "scene" && rightRail && (
        <Drawer title={inCombat ? "Combat" : "The Scene"} side="right" onClose={() => setOpenDrawer(null)}>
          {rightRail}
        </Drawer>
      )}

      {/* Dice roller + table chat live in an on-demand drawer so the table reads calm. */}
      {showTools && (
        <Drawer title="Dice & Table" onClose={() => setShowTools(false)} side="right">
          <DiceTray />
          <TableChat chat={chat} you={you} send={send} />
        </Drawer>
      )}

      {showCompendium && (
        <CompendiumDrawer gameId={gameId} party={snapshot?.party ?? []}
          onOpenSheet={openSheet} art={art} onClose={() => setShowCompendium(false)} />
      )}

      {(sheetChar || sheetLoading) && (
        <Drawer
          title={sheetChar?.name || "Character"}
          onClose={() => setSheetChar(null)}
          actions={sheetChar && (
            <div className="flex" style={{ gap: 6 }}>
              <button className="btn btn--ghost btn--sm" disabled={sheetBusy}
                onClick={() => setAddingItem(true)}>＋ Item</button>
              <button className="btn btn--ghost btn--sm"
                onClick={() => setEditChar(sheetChar)}>✎ Edit</button>
              {sheetChar.level < 20 && (
                <button className="btn btn--primary btn--sm" onClick={() => setLevelUpChar(sheetChar)}>
                  ⭐ Level Up
                </button>
              )}
            </div>
          )}
        >
          {sheetLoading && !sheetChar ? <CenterLoad /> : sheetChar ? (
            <>
              <PixelArt
                shape="square"
                className="portrait-art"
                alt={sheetChar.name}
                body={{ kind: "character", game_id: gameId, character_id: sheetChar.id }}
                liveUrl={art[`character:${sheetChar.id}`]}
              />
              <CharacterSheet ch={sheetChar} actions={makeSheetActions(sheetChar)}
                gameId={gameId} art={art} />
            </>
          ) : null}
        </Drawer>
      )}

      {addingItem && sheetChar && (
        <AddItemPicker
          onClose={() => setAddingItem(false)}
          onPick={handleAddItem}
        />
      )}

      {levelUpChar && meta && (
        <LevelUpModal
          gameId={gameId}
          character={levelUpChar}
          meta={meta}
          onClose={() => setLevelUpChar(null)}
          onLeveled={(ch) => { setSheetChar(ch); refreshSheet(ch.id); }}
        />
      )}

      {editChar && (
        <CharacterEditModal
          gameId={gameId}
          character={editChar}
          onClose={() => setEditChar(null)}
          onSaved={(ch) => { setSheetChar(ch); setEditChar(null); }}
        />
      )}

      {showRest && (
        <RestModal
          gameId={gameId}
          party={snapshot?.party ?? []}
          onClose={() => setShowRest(false)}
        />
      )}
    </>
  );
}

// The conductor's baton, made visible above the composer. "mine" → a warm, prominent
// "your turn" card with the DM's cue; "other" → a quiet "waiting for X" line (the table
// stays open, so the composer below stays usable, just de-emphasized); "open" → a gentle
// hint that anyone may act. "ai" renders nothing — the thinking indicator covers it.
function SpotlightBanner({
  spotlight,
}: {
  spotlight: { kind: "mine" | "other" | "ai" | "open"; name: string; cue: string };
}) {
  if (spotlight.kind === "ai") return null;
  if (spotlight.kind === "mine") {
    return (
      <div className="spotlight spotlight--mine card" role="status" aria-live="polite"
        style={{
          margin: "0 0 8px", padding: "10px 14px", borderRadius: "var(--radius)",
          borderLeft: "3px solid var(--gold)", background: "var(--bg-raised)",
          boxShadow: "var(--glow-ember)",
        }}>
        <div style={{ fontFamily: "var(--serif)", fontSize: "var(--fs-md)", color: "var(--gold-bright)" }}>
          ✨ Your turn, {spotlight.name}
        </div>
        {spotlight.cue && (
          <div className="muted" style={{ fontSize: "var(--fs-sm)", marginTop: 3, lineHeight: 1.45 }}>
            {spotlight.cue}
          </div>
        )}
      </div>
    );
  }
  if (spotlight.kind === "other") {
    return (
      <div className="spotlight spotlight--wait" role="status"
        style={{ margin: "0 0 8px", padding: "6px 12px", fontSize: "var(--fs-sm)" }}>
        <span className="muted">Waiting for <b style={{ color: "var(--ink-soft)" }}>{spotlight.name}</b>…</span>
        {spotlight.cue && <span className="muted" style={{ marginLeft: 6, fontStyle: "italic" }}>{spotlight.cue}</span>}
      </div>
    );
  }
  // open floor
  return (
    <div className="spotlight spotlight--open muted" role="status"
      style={{ margin: "0 0 8px", padding: "4px 12px", fontSize: "var(--fs-xs)", letterSpacing: 0.3 }}>
      ✦ The floor is open — anyone may act.
    </div>
  );
}

function ConnPill({ conn }: { conn: string }) {
  const label = conn === "open" ? "Connected" : conn === "reconnecting" ? "Reconnecting…" :
    conn === "connecting" ? "Connecting…" : "Offline";
  return (
    <span className="conn-pill">
      <span className={`conn-dot conn-dot--${conn}`} /> {label}
    </span>
  );
}

function PresencePill({ present }: { present: { id: string; display_name: string }[] }) {
  if (present.length === 0) return null;
  const names = present.map((p) => p.display_name).join(", ");
  return (
    <span className="presence-pill" title={`At the table: ${names}`}>
      👥 {present.length}
      <span className="presence-names">{names}</span>
    </span>
  );
}

function TableChat({
  chat, you, send,
}: {
  chat: ChatMessage[];
  you: AuthUser | null;
  send: (m: WsClientMessage) => boolean;
}) {
  const [text, setText] = useState("");
  function submit(e: React.FormEvent) {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    if (send({ action: "chat", text: t })) setText("");
  }
  return (
    <div className="table-chat">
      <div className="table-chat__head">Table Chat <span className="muted">(out of character)</span></div>
      <div className="table-chat__log">
        {chat.length === 0 ? (
          <div className="table-chat__empty">Say hi to the others at your table…</div>
        ) : (
          chat.map((m) => (
            <div key={m.uid} className={`table-chat__msg${you && m.userId === you.id ? " is-me" : ""}`}>
              <span className="table-chat__who">{m.who}</span>
              <span className="table-chat__text">{m.text}</span>
            </div>
          ))
        )}
      </div>
      <form className="table-chat__form" onSubmit={submit}>
        <input className="table-chat__input" value={text} maxLength={500}
          placeholder="Message the table…" onChange={(e) => setText(e.target.value)} />
        <button className="btn btn--ghost btn--sm" type="submit">Send</button>
      </form>
    </div>
  );
}

// A tiny HP indicator: N dots whose fill count tracks a health fraction. Foe fractions
// come from the coarse (bucketed) wound_frac, so this never leaks an exact number.
function HpDots({ frac, gone }: { frac: number; gone: boolean }) {
  const N = 4;
  const filled = gone ? 0 : Math.max(1, Math.round(frac * N));
  const band = frac <= 0.25 ? "crit" : frac <= 0.5 ? "low" : "";
  return (
    <span className="mini-tracker__hpdots" aria-hidden>
      {Array.from({ length: N }, (_, i) => (
        <span key={i} className={`mini-tracker__dot${i < filled ? (band ? ` ${band}` : "") : " gone"}`} />
      ))}
    </span>
  );
}

function MiniUnit({ c, current }: { c: Combatant; current: boolean }) {
  // Party combatants expose exact hp; foes are redacted to a coarse wound band.
  const foe = c.side !== "party";
  const frac = foe
    ? (typeof c.wound_frac === "number" ? c.wound_frac : 1)
    : (c.max_hp > 0 ? c.hp / c.max_hp : 0);
  const gone = foe ? (c.wound_tier === "down" || !c.alive) : (c.hp <= 0 || !c.alive);
  return (
    <span className={`mini-tracker__unit${current ? " current" : ""}`}>
      <span className="mini-tracker__name">{c.name}</span>
      <HpDots frac={frac} gone={gone} />
    </span>
  );
}

// One-line combat strip for narrow widths (the tracker rail is drawered away). Round +
// current combatant + who's up next, each with fuzzy HP dots — no exact foe numbers.
function MiniTracker({ combat }: { combat: Combat }) {
  const order = combat.order;
  if (order.length === 0) return null;
  const curIdx = Math.max(0, order.findIndex((c) => c.is_current));
  const current = order[curIdx];
  const next = order[(curIdx + 1) % order.length];
  return (
    <div className="mini-tracker" role="status" aria-label={`Combat round ${combat.round}`}>
      <span className="mini-tracker__round">Round {combat.round}</span>
      <MiniUnit c={current} current />
      {next && next.id !== current.id && (
        <>
          <span className="mini-tracker__sep">→</span>
          <MiniUnit c={next} current={false} />
        </>
      )}
    </div>
  );
}

// Compact party list for the narrow-width "Party" drawer. Mirrors PartyRail's data +
// classes (name / HP / AC / conditions / take-turn) but omits the portrait & float-number
// chrome; also surfaces the level-up affordance for any PC whose XP qualifies.
function DrawerPartyList({
  party, aiCharacterIds, levelUpIds, spotlightId, busy, send, onOpenSheet, onLevelUp,
}: {
  party: PartyMember[];
  aiCharacterIds: Set<string>;
  levelUpIds: Set<string>;
  spotlightId: string | null;
  busy: boolean;
  send: (msg: WsClientMessage) => boolean;
  onOpenSheet: (characterId: string) => void;
  onLevelUp: (characterId: string) => void;
}) {
  return (
    <div className="rail-section">
      <div className="rail-head"><span>The Party</span><span>{party.length}</span></div>
      {party.length === 0 && <div className="muted" style={{ fontSize: 13 }}>No one is seated.</div>}
      {party.map((p) => {
        const isAi = aiCharacterIds.has(p.id);
        const down = p.hp <= 0;
        const canLevel = levelUpIds.has(p.id);
        const spotlighted = spotlightId === p.id;
        return (
          <div key={p.id} className={`pc-row${down ? " pc-row--down" : ""}${spotlighted ? " pc-row--spotlight" : ""}`}
            onClick={() => onOpenSheet(p.id)}
            style={{ position: "relative", ...(spotlighted ? { boxShadow: "inset 0 0 0 1.5px var(--gold)", borderRadius: "var(--radius)" } : null) }}>
            <div className="pc-row__line" style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <span className="pc-row__name">{p.name}</span>
              {spotlighted && <span title="It's their moment" aria-label="spotlighted" style={{ fontSize: "var(--fs-sm)" }}>✨</span>}
              {isAi && <span className="chip chip--ai">AI</span>}
              {canLevel && <span className="chip chip--gold" title="Ready to level up">⭐</span>}
              <span className="pc-row__spacer" />
              <span className="pc-row__ac" title="Armor Class">🛡 {p.ac}</span>
            </div>
            <div className="pc-row__hpwrap">
              <HpBar hp={p.hp} max={p.max_hp} temp={p.temp_hp} />
              <span className="pc-row__hp num">{p.hp}/{p.max_hp}{p.temp_hp > 0 ? `+${p.temp_hp}` : ""}</span>
            </div>
            {(p.conditions.length > 0 || p.concentration) && (
              <div className="pc-row__meta">
                {p.concentration && <span className="chip chip--conc">◎ {p.concentration}</span>}
                {p.conditions.map((c) => <span key={c} className="chip chip--cond">{c}</span>)}
              </div>
            )}
            {(canLevel || isAi) && (
              <div className="flex" style={{ gap: 6, marginTop: 6 }}>
                {canLevel && (
                  <button className="btn btn--primary btn--sm"
                    onClick={(e) => { e.stopPropagation(); onLevelUp(p.id); }}>⭐ Level up</button>
                )}
                {isAi && (
                  <button className="btn btn--sm pc-row__turn" disabled={busy}
                    onClick={(e) => { e.stopPropagation(); send({ action: "ai_player_turn", character_id: p.id }); }}>
                    Take turn
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
